import { Loader2, Smartphone } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";

export interface NequiWaitingScreenProps {
  title: string;
  /** One sentence; the caller interpolates the payer's phone, which on this rail IS the account charged. */
  body: string;
  /** Reassurance that the screen is live — it flips on its own when the webhook lands. */
  liveLabel: string;
  /** Already formatted with its currency symbol or code, or `null` while unknown. */
  amount: string | null;
  /** Lets the operator find this collection afterwards. Omit on a surface that shows it elsewhere. */
  reference?: string | null;
  referenceLabel?: string;
  actionLabel: string;
  onNew: () => void;
}

/**
 * The outcome screen of a Nequi collection: a wait state, not an instrument.
 *
 * Every other Colombian rail hands the payer something — a QR to scan, a bank page to authorise on. Nequi
 * hands over nothing: the charge is already in their app and the only thing left is to wait for them to
 * accept it, so this screen's job is to say that and then keep itself current. The host pairs it with
 * `usePayinLiveStatus`, which flips the step the moment the provider webhook settles or rejects the payin.
 *
 * The copy arrives as props rather than from a namespace because the two surfaces address different people:
 * `/app/payins` speaks to a collector about someone else's phone, the COP wallet modal to the wallet owner
 * about their own. Only the geometry is shared — which is the point, since it was written twice before.
 */
export function NequiWaitingScreen({
  title,
  body,
  liveLabel,
  amount,
  reference,
  referenceLabel,
  actionLabel,
  onNew,
}: NequiWaitingScreenProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-14 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-primary/10">
        <Smartphone className="size-7 text-primary" aria-hidden="true" />
      </div>
      <p className="text-base font-semibold text-foreground">{title}</p>
      <p className="max-w-sm text-sm text-muted-foreground">{body}</p>
      {amount && <p className="font-mono text-sm font-semibold text-foreground">{amount}</p>}
      {reference && (
        <p className="text-xs text-muted-foreground">
          {referenceLabel && <span>{referenceLabel}: </span>}
          <span className="font-mono font-semibold text-foreground">{reference}</span>
        </p>
      )}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        {liveLabel}
      </div>
      {/* Creating another collection does not cancel this one — it stays live in the payer's app, which is
          why the reference above matters. */}
      <ColocaButton variant="ghost" onClick={onNew}>
        {actionLabel}
      </ColocaButton>
    </div>
  );
}
